import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

type CurtainState = 'hidden' | 'entering' | 'exiting';

interface NavigationContextType {
  navigateTo: (path: string) => void;
  triggerCurtain: (callback: () => void) => void;
  curtainState: CurtainState;
  isTransitioning: boolean;
  currentPath: string;
  previousPath: string | null;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [curtainState, setCurtainState] = useState<CurtainState>('hidden');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousPath, setPreviousPath] = useState<string | null>(null);
  const stateRef = useRef({ currentPath: location.pathname, isTransitioning: false });

  // Disable browser's automatic scroll restoration so we control it ourselves
  React.useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);


  // Update ref when state changes; track previous path on real path change
  const prevPathRef = useRef(location.pathname);
  React.useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      setPreviousPath(prevPathRef.current);
      prevPathRef.current = location.pathname;
    }
    stateRef.current = { currentPath: location.pathname, isTransitioning };
  }, [location.pathname, isTransitioning]);

  const navigateTo = useCallback((path: string) => {
    const { currentPath, isTransitioning: transitioning } = stateRef.current;
    
    // Normalize paths for comparison
    const normalizedPath = path === '/' ? '/' : path;
    const normalizedCurrent = currentPath === '/' ? '/' : currentPath;
    
    if (normalizedPath === normalizedCurrent || transitioning) return;

    setIsTransitioning(true);
    setCurtainState('entering');

    setTimeout(() => {
      // Scroll to top BEFORE navigating so the new page never paints at the
      // previous scroll position (avoids the "flash of bottom-of-page" bug).
      window.scrollTo(0, 0);
      navigate(path);
      // Re-assert on the next frame in case the browser restores scroll async.
      requestAnimationFrame(() => window.scrollTo(0, 0));

      setTimeout(() => {
        setCurtainState('exiting');

        setTimeout(() => {
          setCurtainState('hidden');
          setIsTransitioning(false);
        }, 1000);
      }, 100);
    }, 1000);
  }, [navigate]);

  // Generic curtain trigger for actions like language switch
  const triggerCurtain = useCallback((callback: () => void) => {
    if (stateRef.current.isTransitioning) return;

    setIsTransitioning(true);
    setCurtainState('entering');

    setTimeout(() => {
      callback();
      
      setTimeout(() => {
        setCurtainState('exiting');
        
        setTimeout(() => {
          setCurtainState('hidden');
          setIsTransitioning(false);
        }, 1000);
      }, 100);
    }, 1000);
  }, []);

  return (
    <NavigationContext.Provider value={{ 
      navigateTo, 
      triggerCurtain,
      curtainState, 
      isTransitioning,
      currentPath: location.pathname,
      previousPath,
    }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
